import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { SwipePayload } from '@shared/models';

@Directive({
  selector: '[b2cSwipe]',
  host: {
    '(mousedown)': 'onMouseDown($event)',
    '(document:mousemove)': 'onMouseMove($event)',
    '(document:mouseup)': 'onMouseUp($event)',
  },
})
export class SwipeDirective implements OnInit, OnDestroy {
  swipeStart = output<void>();
  swipeMove = output<SwipePayload>();
  swipeEnd = output<SwipePayload>();

  private el = inject(ElementRef);

  private startX = 0;
  private startY = 0;
  private startT = 0;
  private lastX = 0;
  private lastT = 0;
  private tracking = false;
  private isHorizontalSwipe: boolean | null = null;

  // Bound handlers for touch events
  private boundTouchStart = this.onTouchStart.bind(this);
  private boundTouchMove = this.onTouchMove.bind(this);
  private boundTouchEnd = this.onTouchEnd.bind(this);

  ngOnInit(): void {
    const el = this.el.nativeElement;
    // Critical for mobile: tell browser we handle horizontal swipes
    el.style.touchAction = 'pan-y';

    // Use passive: false to allow preventDefault on touchmove
    el.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    el.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    el.addEventListener('touchend', this.boundTouchEnd, { passive: true });
    el.addEventListener('touchcancel', this.boundTouchEnd, { passive: true });
  }

  ngOnDestroy(): void {
    const el = this.el.nativeElement;
    el.removeEventListener('touchstart', this.boundTouchStart);
    el.removeEventListener('touchmove', this.boundTouchMove);
    el.removeEventListener('touchend', this.boundTouchEnd);
    el.removeEventListener('touchcancel', this.boundTouchEnd);
  }

  // Mouse events for desktop
  onMouseDown(ev: MouseEvent): void {
    if (ev.button !== 0) return;
    this.startTracking(ev.clientX, ev.clientY);
  }

  onMouseMove(ev: MouseEvent): void {
    if (!this.tracking) return;
    this.handleMove(ev.clientX, ev.clientY);
  }

  onMouseUp(ev: MouseEvent): void {
    if (!this.tracking) return;
    this.endTracking(ev.clientX);
  }

  // Touch event handlers
  private onTouchStart(ev: TouchEvent): void {
    if (ev.touches.length !== 1) return;
    const touch = ev.touches[0];
    this.startTracking(touch.clientX, touch.clientY);
  }

  private onTouchMove(ev: TouchEvent): void {
    if (!this.tracking || ev.touches.length !== 1) return;
    const touch = ev.touches[0];
    const shouldPrevent = this.handleMove(touch.clientX, touch.clientY);

    // Prevent scrolling if this is a horizontal swipe
    if (shouldPrevent) {
      ev.preventDefault();
    }
  }

  private onTouchEnd(ev: TouchEvent): void {
    if (!this.tracking) return;
    const touch = ev.changedTouches[0];
    this.endTracking(touch?.clientX ?? this.lastX);
  }

  // Common tracking logic
  private startTracking(x: number, y: number): void {
    this.tracking = true;
    this.isHorizontalSwipe = null;
    this.startX = x;
    this.startY = y;
    this.startT = performance.now();
    this.lastX = x;
    this.lastT = this.startT;
    this.swipeStart.emit();
  }

  private handleMove(x: number, y: number): boolean {
    // Determine swipe direction on first significant movement
    if (this.isHorizontalSwipe === null) {
      const deltaX = Math.abs(x - this.startX);
      const deltaY = Math.abs(y - this.startY);

      if (deltaX > 8 || deltaY > 8) {
        this.isHorizontalSwipe = deltaX > deltaY;

        // If vertical swipe, stop tracking and reset
        if (!this.isHorizontalSwipe) {
          this.tracking = false;
          // Emit end with 0 velocity to snap back
          const currentDelta = x - this.startX;
          this.swipeEnd.emit({ deltaX: currentDelta, velocityX: 0 });
          return false;
        }
      }
    }

    if (this.isHorizontalSwipe === false) return false;

    this.lastX = x;
    this.lastT = performance.now();

    const deltaX = this.lastX - this.startX;
    const totalDt = Math.max(1, this.lastT - this.startT);
    const velocityX = deltaX / totalDt;

    this.swipeMove.emit({ deltaX, velocityX });

    return this.isHorizontalSwipe === true;
  }

  private endTracking(x: number): void {
    this.tracking = false;

    this.lastX = x;
    this.lastT = performance.now();

    const deltaX = this.lastX - this.startX;
    const totalDt = Math.max(1, this.lastT - this.startT);
    const velocityX = deltaX / totalDt;

    this.swipeEnd.emit({ deltaX, velocityX });
    this.isHorizontalSwipe = null;
  }
}
